/**
 * Akıllı tahta çizimlerinin oturum-içi kalıcılığı. Çizimler vektör (nokta dizisi) olarak
 * sessionStorage'da test+soru bazında saklanır — bitmap değil, maliyet ihmal edilebilir.
 *
 * Davranış: aynı sekme açık kaldıkça (F5 dahil) çizim durur; farklı soru/teste geçince ilgili
 * anahtarın çizimi gösterilir; ÇIKIŞTA {@link clearTahtaDrawings} ile tümü silinir (clearAuth'a bağlı).
 */

export const TAHTA_DRAW_PREFIX = "tahta.draw.";

export interface StoredPoint {
  x: number;
  y: number;
}

export interface StoredAction {
  tool: string;
  points: StoredPoint[];
  color: string;
  width: number;
}

export const drawingStorageKey = (testId: string, questionId: string) =>
  `${TAHTA_DRAW_PREFIX}${testId}.${questionId}`;

export const loadDrawing = (key: string): StoredAction[] => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredAction[]) : [];
  } catch {
    return [];
  }
};

export const saveDrawing = (key: string, actions: StoredAction[]) => {
  try {
    if (actions.length === 0) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, JSON.stringify(actions));
  } catch {
    /* kota/serileştirme hatası çizimi engellemesin */
  }
};

/* ------------------------------------------------------------------ */
/* Çizim aracı tercihleri (renk/kalınlık) — kalıcı, localStorage        */
/* ------------------------------------------------------------------ */

const TAHTA_SETTINGS_KEY = "tahta.draw.settings";

export interface TahtaDrawSettings {
  /** Araç bazında son seçilen renk (kalem siyah, fosforlu sarı vb. birbirini ezmez). */
  toolColors: Record<string, string>;
  thickness: number;
  /** Silgi çapı (px, CSS). */
  eraserSize: number;
}

export const loadDrawSettings = (): Partial<TahtaDrawSettings> => {
  try {
    const raw = localStorage.getItem(TAHTA_SETTINGS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Partial<TahtaDrawSettings>) : {};
  } catch {
    return {};
  }
};

export const saveDrawSettings = (settings: TahtaDrawSettings) => {
  try {
    localStorage.setItem(TAHTA_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* kota hatası çizimi engellemesin */
  }
};

/* ------------------------------------------------------------------ */
/* Çizim paneli konumu (sürükle-bırak) — kalıcı, localStorage           */
/* ------------------------------------------------------------------ */

/** Sürüm eki: panel dikeyden yatay çubuğa dönüştü; eski dikey konumlar yeni çubuğu yanlış yere koyardı. */
const TAHTA_PANEL_POS_KEY = "tahta.draw.panelPos.v2";

export interface TahtaPanelPos {
  /** Viewport'a göre sol/üst köşe (px, position: fixed). */
  x: number;
  y: number;
}

export const loadDrawPanelPos = (): TahtaPanelPos | null => {
  try {
    const raw = localStorage.getItem(TAHTA_PANEL_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.x === "number" && typeof parsed.y === "number") {
      return { x: parsed.x, y: parsed.y };
    }
    return null;
  } catch {
    return null;
  }
};

export const saveDrawPanelPos = (pos: TahtaPanelPos) => {
  try {
    localStorage.setItem(TAHTA_PANEL_POS_KEY, JSON.stringify(pos));
  } catch {
    /* kota hatası çizimi engellemesin */
  }
};

/** Çıkışta (clearAuth) tüm tahta çizimlerini sil. */
export const clearTahtaDrawings = () => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(TAHTA_DRAW_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* yoksay */
  }
};
